import {AsyncDataLoading} from '@frontend/components/AsyncDataLoading'
import {trans} from '@frontend/helpers'
import {
    mdiDeleteOutline,
    mdiDotsVertical,
    mdiEye,
} from '@mdi/js'
import {
    useEffect,
    useState,
} from 'react'
import {Button} from 'swayok-react-mdb-ui-kit/components/Button/Button'
import {Card} from 'swayok-react-mdb-ui-kit/components/Card/Card'
import {CardBody} from 'swayok-react-mdb-ui-kit/components/Card/CardBody'
import {CardHeader} from 'swayok-react-mdb-ui-kit/components/Card/CardHeader'
import {CardTitle} from 'swayok-react-mdb-ui-kit/components/Card/CardTitle'
import {Collapse} from 'swayok-react-mdb-ui-kit/components/Collapse/Collapse'
import {Dropdown} from 'swayok-react-mdb-ui-kit/components/Dropdown/Dropdown'
import {DropdownHeader} from 'swayok-react-mdb-ui-kit/components/Dropdown/DropdownHeader'
import {DropdownItem} from 'swayok-react-mdb-ui-kit/components/Dropdown/DropdownItem'
import {DropdownMenu} from 'swayok-react-mdb-ui-kit/components/Dropdown/DropdownMenu'
import {DropdownToggle} from 'swayok-react-mdb-ui-kit/components/Dropdown/DropdownToggle'
import {Icon} from 'swayok-react-mdb-ui-kit/components/Icon/Icon'
import {ButtonsSwitchInput} from 'swayok-react-mdb-ui-kit/components/Input/ButtonsSwitchInput'
import {handleErrorResponse} from 'swayok-react-mdb-ui-kit/helpers/api/ApiRequestErrorHelpers'
import type {ApiError} from 'swayok-react-mdb-ui-kit/services/ApiRequestService'
import {ChatsApi} from '../ChatsApi'
import type {
    ChatDetailsData,
    ChatsFilter,
} from '../ChatsModuleTypes'
import {ChatsListItem} from './ChatsListItem'

interface Props {
    miniChat?: boolean
    filter: ChatsFilter
    chats: ChatDetailsData[] | null
    isAllChatsLoaded: boolean
    // Перейти к сообщениям чата.
    setChat: (chat: ChatDetailsData | null) => void
    // Сохранить загруженные чаты.
    setChats: (filter: ChatsFilter, chats: ChatDetailsData[], isAllChatsLoaded: boolean) => void// Удаление чата.
    // Удалить чаты.
    onDelete: (chats: number[]) => void
    // Прочитать все сообщения для списка чатов.
    onReadAllMessages: (chats: number[]) => void
    // Показать окно добавления отзыва о продавце.
    onAddReview: (chat: ChatDetailsData) => void
    // Состояние выполнения действия с чатами.
    isProcessing?: boolean
}

const limitPerPage: number = 20

// Список чатов.
export function ChatsList(props: Props) {

    const {
        miniChat = false,
        filter: filterFromProps,
        chats,
        isAllChatsLoaded = false,
        setChat,
        setChats,
        onDelete,
        onReadAllMessages,
        onAddReview,
        isProcessing = false,
    } = props

    // Список выбранных чатов.
    const [
        selectedItems,
        setSelectedItems,
    ] = useState<number[]>([])

    // Фильтр сообщений.
    const [
        filter,
        setFilter,
    ] = useState<ChatsFilter>(filterFromProps)

    // Состояния загрузки списка чатов.
    const [
        isLoading,
        setIsLoading,
    ] = useState<boolean>(chats === null)
    const [
        isLoadingError,
        setIsLoadingError,
    ] = useState<boolean>(false)
    const [
        isLoadingMoreChats,
        setIsLoadingMoreChats,
    ] = useState<boolean>(false)

    // Загрузка списка чатов.
    const loadChats = (nextPage: boolean = false, newFilter?: ChatsFilter) => {
        if (!chats) {
            nextPage = false
        }
        setIsLoading(!nextPage)
        setIsLoadingMoreChats(nextPage)
        setIsLoadingError(false)
        if (newFilter === undefined) {
            newFilter = filter
        }
        ChatsApi.getChatsList(
            newFilter,
            limitPerPage,
            nextPage ? chats?.length ?? 0 : 0
        )
            .then(data => {
                setChats(
                    filter,
                    nextPage ? (chats ?? []).concat(data.records) : data.records,
                    data.records.length < limitPerPage
                )
            })
            .catch((error: ApiError) => {
                if (!nextPage) {
                    setIsLoadingError(true)
                } else {
                    handleErrorResponse(error)
                }
            })
            .finally(() => {
                setIsLoading(false)
                setIsLoadingMoreChats(false)
            })
    }

    // Загружаем первоначальный список чатов.
    useEffect(() => {
        if (chats === null) {
            loadChats(false)
        }
    }, [])

    // Выделить или снять выделение с объявления.
    const toggleSelection = (id: number): void => {
        setSelectedItems((ids: number[]): number[] => {
            const newIds: number[] = ids.slice()
            if (newIds.includes(id)) {
                newIds.splice(newIds.indexOf(id), 1)
            } else {
                newIds.push(id)
            }
            return newIds
        })
    }

    const content = (
        <AsyncDataLoading
            loading={isLoading}
            error={isLoadingError}
            onReload={loadChats}
            render={() => {
                if (chats?.length === 0) {
                    return (
                        <div className="py-6 px-5 text-center fs-5">
                            {trans().cabinet.chats.no_chats}
                        </div>
                    )
                }
                return (
                    <div className="p-2">
                        {chats?.map((chat: ChatDetailsData, index: number) => (
                            <ChatsListItem
                                key={'chat' + chat.id}
                                miniChat={miniChat}
                                chat={chat}
                                selected={selectedItems.includes(chat.id)}
                                isLast={index + 1 === chats?.length}
                                disabled={isProcessing}
                                onSelect={toggleSelection}
                                onChatOpen={setChat}
                                onDelete={chat => onDelete([chat.id])}
                                onReadAllMessages={chat => onReadAllMessages([chat.id])}
                                onAddReview={onAddReview}
                            />
                        ))}
                        <Collapse
                            show={!isAllChatsLoaded}
                            showImmediately
                        >
                            <div className="flex-center pt-3">
                                <Button
                                    color="gray"
                                    onClick={() => loadChats(true)}
                                    disabled={isLoadingMoreChats}
                                >
                                    {trans().common.load_more_records}
                                </Button>
                            </div>
                        </Collapse>
                    </div>
                )
            }}
        />
    )

    if (miniChat) {
        return (
            <div className="cabinet-chats">
                {content}
            </div>
        )
    }

    return (
        <Card className="cabinet-chats">
            <CardHeader className="pt-4 d-flex flex-column align-items-stretch position-relative flex-sm-row align-items-sm-center justify-content-start">
                <CardTitle className="flex-1 text-center text-sm-start">
                    {trans().cabinet.chats.header}
                </CardTitle>
                <ButtonsSwitchInput<ChatsFilter>
                    className="m-0 mt-3 mt-sm-0 flex-shrink-0 flex-center"
                    value={filter}
                    options={[
                        {
                            label: trans().cabinet.chats.filter.all,
                            value: null,
                        },
                        {
                            label: trans().cabinet.chats.filter.not_viewed,
                            value: 'not_viewed',
                        },
                    ]}
                    onChange={(value: ChatsFilter) => {
                        setFilter(value)
                        loadChats(false, value)
                    }}
                />
                <div className="cabinet-chats-dropdown ms-2 flex-shrink-0">
                    <Dropdown
                        focusFirstItemOnOpen={false}
                    >
                        <DropdownToggle
                            color="icon"
                            className="link-muted btn-hover-theme p-2 me-n2"
                        >
                            <Icon
                                path={mdiDotsVertical}
                                rotate={90}
                            />
                        </DropdownToggle>
                        <DropdownMenu
                            drop="down"
                            align="end"
                            offset={4}
                            shadow="2-strong"
                        >
                            <DropdownHeader className="text-center text-muted">
                                {trans().cabinet.chats.chats_selected(selectedItems.length)}
                            </DropdownHeader>
                            <DropdownItem
                                className="with-icon-flex"
                                disabled={selectedItems.length === 0 || isProcessing}
                                onClick={() => {
                                    onReadAllMessages(selectedItems)
                                    setSelectedItems([])
                                }}
                            >
                                <Icon
                                    path={mdiEye}
                                    size={20}
                                    className="me-2"
                                />
                                {trans().cabinet.chats.action.read_selected_chats}
                            </DropdownItem>
                            <DropdownItem
                                className="with-icon-flex"
                                disabled={selectedItems.length === 0 || isProcessing}
                                onClick={() => {
                                    if (confirm(trans().cabinet.chats.action.delete_selected_chats_confirmation)) {
                                        onDelete(selectedItems)
                                        setSelectedItems([])
                                    }
                                }}
                            >
                                <Icon
                                    path={mdiDeleteOutline}
                                    size={20}
                                    className="me-2"
                                />
                                {trans().cabinet.chats.action.delete_selected_chats}
                            </DropdownItem>
                        </DropdownMenu>
                    </Dropdown>
                </div>
            </CardHeader>
            <CardBody className="p-0">
                {content}
            </CardBody>
        </Card>
    )
}
